import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { buildBetaFeedbackFilename } from "@/lib/betaFeedback/filename";

/*
 * Admin — Beta Feedback library (Stage 5 — 2026-02).
 *
 * Read-only paginated listing of every submitted Beta Feedback
 * record. Each row exposes a PDF action that hits
 * `GET /api/beta-feedback/{feedback_id}/pdf` (admin-extended in
 * Stage 5). No edit / delete controls — feedback is preserved
 * verbatim as submitted by the tester.
 *
 * Filters: user (email/first_name substring), language, beta code,
 * date range. Default sort is newest submission first, enforced by
 * the backend.
 */

const LANG_LABELS = {
  en: "English",
  "pt-BR": "Português (Brasil)",
  "es-419": "Español (Latinoamérica)",
  "fr-CA": "Français (Canada)",
};

const LANG_OPTIONS = ["", "en", "pt-BR", "es-419", "fr-CA"];

function fmt(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function displayName(row) {
  // "Resolve the stored user_id to the user's current display/name
  // information using existing account data. If an account name is
  // unavailable, use the existing Admin fallback convention…"
  // The rest of the Admin panel uses "—" as its unset marker; we
  // fall back to email so admins can still identify the tester.
  return row.user_first_name || row.user_email || "—";
}

export default function AdminBetaFeedback() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [language, setLanguage] = useState("");
  const [betaCode, setBetaCode] = useState("");
  const [dateFromDraft, setDateFromDraft] = useState("");
  const [dateToDraft, setDateToDraft] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setErrored(false);
    try {
      const params = { page, page_size: pageSize };
      if (q) params.q = q;
      if (language) params.language = language;
      if (betaCode) params.beta_code = betaCode;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const { data } = await api.get("/admin/beta-feedback", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (ex) {
      setErrored(true);
      // Backend spec: "Do not expose backend error details."
      toast.error("Unable to load feedback right now.");
      // Log the actual detail for admin operators via console only.
      console.warn("admin beta feedback load failed:", formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, language, betaCode, dateFrom, dateTo]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyFilters = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setPage(1);
    setQ(qDraft.trim());
    setDateFrom(dateFromDraft);
    setDateTo(dateToDraft);
  };

  const clearFilters = () => {
    setPage(1);
    setQDraft("");
    setQ("");
    setLanguage("");
    setBetaCode("");
    setDateFromDraft("");
    setDateToDraft("");
    setDateFrom("");
    setDateTo("");
  };

  const openPdf = async (row) => {
    // Fetch as a blob with the admin's session cookie, then hand off
    // to the browser via an object URL. We use `window.open` so admins
    // preview the PDF in-tab, matching admin panel conventions
    // (audit downloads work the same way).
    try {
      const res = await api.get(`/beta-feedback/${row.feedback_id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const filename = buildBetaFeedbackFilename(
        row.user_first_name || row.user_email,
        row.submitted_at,
      );
      const win = window.open(url, "_blank", "noopener,noreferrer");
      // Some browsers/pop-up blockers may swallow window.open — fall
      // back to a direct anchor click.
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Release the object URL after a short delay so the tab has
      // time to load the blob first.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (ex) {
      toast.error("Could not load that PDF.");
      console.warn("admin pdf failed:", formatApiErrorDetail(ex));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-beta-feedback">
      <AdminNav title="Beta Feedback" />

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <form
          onSubmit={applyFilters}
          className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-3"
          data-testid="admin-bf-filters"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
            Filters
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">
                User (email or name)
              </span>
              <input
                type="text"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="e.g. alice or @example.com"
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-bf-filter-q"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">Beta Code</span>
              <input
                type="text"
                value={betaCode}
                onChange={(e) => { setPage(1); setBetaCode(e.target.value); }}
                placeholder="Substring match"
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-bf-filter-beta-code"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">Language</span>
              <select
                value={language}
                onChange={(e) => { setPage(1); setLanguage(e.target.value); }}
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm bg-white"
                data-testid="admin-bf-filter-language"
              >
                {LANG_OPTIONS.map((code) => (
                  <option key={code || "any"} value={code}>
                    {code === "" ? "Any language" : LANG_LABELS[code] || code}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">From (local)</span>
              <input
                type="datetime-local"
                value={dateFromDraft}
                onChange={(e) => setDateFromDraft(e.target.value)}
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-bf-filter-date-from"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-[color:var(--text-secondary)] mb-1">To (local)</span>
              <input
                type="datetime-local"
                value={dateToDraft}
                onChange={(e) => setDateToDraft(e.target.value)}
                className="w-full rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
                data-testid="admin-bf-filter-date-to"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2 text-xs font-medium"
              data-testid="admin-bf-apply-filters"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-full border border-[color:var(--border-stone)] px-4 py-2 text-xs hover:bg-[color:var(--bg-surface-hover)]"
              data-testid="admin-bf-clear-filters"
            >
              Clear
            </button>
            <div className="ml-auto text-xs text-[color:var(--text-secondary)]" data-testid="admin-bf-total">
              {total} submission{total === 1 ? "" : "s"}
            </div>
          </div>
        </form>

        <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-stone)]">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[color:var(--text-primary)]">
              Submissions — newest first
            </div>
            <button
              type="button"
              onClick={refresh}
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs hover:bg-[color:var(--bg-surface-hover)]"
              data-testid="admin-bf-refresh"
            >
              Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]" data-testid="admin-bf-loading">
              Loading…
            </div>
          ) : errored ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]" data-testid="admin-bf-error">
              Something went wrong loading the feedback library. Try Refresh above.
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]" data-testid="admin-bf-empty">
              {q || betaCode || language || dateFrom || dateTo
                ? "No feedback matches your filters."
                : "No Beta Feedback submissions yet."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-bf-table">
                <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Submitted</th>
                    <th className="text-left px-3 py-2">Beta Code</th>
                    <th className="text-left px-3 py-2">Language</th>
                    <th className="text-left px-3 py-2">Session</th>
                    <th className="text-right px-3 py-2">Feedback PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.feedback_id}
                      className="border-t border-[color:var(--border-stone)] align-top"
                      data-testid={`admin-bf-row-${row.feedback_id}`}
                    >
                      <td className="px-3 py-2">
                        <div
                          className="font-medium text-[color:var(--text-primary)]"
                          data-testid={`admin-bf-row-${row.feedback_id}-user`}
                        >
                          {displayName(row)}
                        </div>
                        {row.user_email && (
                          <div className="text-[10px] text-[color:var(--text-secondary)] break-all">
                            {row.user_email}
                          </div>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 whitespace-nowrap"
                        data-testid={`admin-bf-row-${row.feedback_id}-submitted`}
                      >
                        {fmt(row.submitted_at)}
                      </td>
                      <td
                        className="px-3 py-2 font-mono break-all"
                        data-testid={`admin-bf-row-${row.feedback_id}-code`}
                      >
                        {row.beta_code || "—"}
                      </td>
                      <td
                        className="px-3 py-2 whitespace-nowrap"
                        data-testid={`admin-bf-row-${row.feedback_id}-language`}
                      >
                        {LANG_LABELS[row.language] || row.language || "—"}
                      </td>
                      <td className="px-3 py-2 font-mono break-all text-[color:var(--text-secondary)]">
                        {row.session_id || "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openPdf(row)}
                          className="text-[color:var(--brand)] underline"
                          data-testid={`admin-bf-row-${row.feedback_id}-pdf`}
                        >
                          View PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="flex items-center justify-between text-xs text-[color:var(--text-secondary)]">
            <div>
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 disabled:opacity-40"
                data-testid="admin-bf-prev-page"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 disabled:opacity-40"
                data-testid="admin-bf-next-page"
              >
                Next →
              </button>
            </div>
          </div>
        )}

        <div className="text-[11px] text-[color:var(--text-secondary)]">
          API base: <span className="font-mono">{API_BASE}</span>
        </div>
      </main>
    </div>
  );
}
